import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Accessibility,
  CheckCircle,
  Globe,
  Eye,
  Keyboard,
  Monitor,
  FileText
} from 'lucide-react';
import { trackPageView } from '../utils/GoogleAnalytics';

/**
 * // * Required by Israeli Standard SI 5568 and WCAG 2.1 AA
 */
function AccessibilityStatement() {
  useEffect(() => {
    trackPageView('/accessibility', 'Accessibility Statement');
  }, []);

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
          <Accessibility className="w-8 h-8 text-blue-600" aria-hidden="true" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Accessibility Statement
        </h1>
        <p className="text-gray-600">
          MidoLABS.ai is committed to ensuring digital accessibility for people with disabilities.
        </p>
      </div>

      {/* Content */}
      <div className="space-y-8">

        {/* Our Commitment */}
        <section className="bg-white rounded-xl border border-gray-200 p-6" aria-labelledby="commitment-heading">
          <h2 id="commitment-heading" className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-500" aria-hidden="true" />
            Our Commitment
          </h2>
          <p className="text-gray-700 leading-relaxed">
            MidoLABS is committed to providing a website that is accessible to the widest possible audience,
            regardless of technology or ability. We are actively working to increase the accessibility and
            usability of our website and in doing so adhere to many of the available standards and guidelines.
          </p>
        </section>

        {/* Standards We Follow */}
        <section className="bg-white rounded-xl border border-gray-200 p-6" aria-labelledby="standards-heading">
          <h2 id="standards-heading" className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-green-500" aria-hidden="true" />
            Standards We Follow
          </h2>
          <p className="text-gray-700 mb-4">
            This website strives to conform to level AA of the World Wide Web Consortium (W3C)
            Web Content Accessibility Guidelines 2.1 (WCAG 2.1) and the Israeli Standard SI 5568.
          </p>
          <ul className="space-y-2" role="list">
            {[
              'WCAG 2.1 Level AA',
              'Israeli Standard SI 5568 Part 1 (Websites)',
              'Section 508 of the Rehabilitation Act'
            ].map((standard, index) => (
              <li key={index} className="flex items-center gap-2 text-gray-700" role="listitem">
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" aria-hidden="true" />
                {standard}
              </li>
            ))}
          </ul>
        </section>

        {/* Accessibility Features */}
        <section className="bg-white rounded-xl border border-gray-200 p-6" aria-labelledby="features-heading">
          <h2 id="features-heading" className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-purple-500" aria-hidden="true" />
            Accessibility Features
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              {
                icon: <Keyboard className="w-5 h-5" />,
                title: 'Keyboard Navigation',
                desc: 'Full keyboard accessibility for all interactive elements'
              },
              {
                icon: <Monitor className="w-5 h-5" />,
                title: 'Screen Reader Support',
                desc: 'Compatible with popular screen readers (JAWS, NVDA, VoiceOver)'
              },
              {
                icon: <Eye className="w-5 h-5" />,
                title: 'Color Contrast',
                desc: 'Text meets WCAG AA contrast requirements (4.5:1 ratio)'
              },
              {
                icon: <Globe className="w-5 h-5" />,
                title: 'Responsive Design',
                desc: 'Works on all devices and screen sizes'
              }
            ].map((feature, index) => (
              <div key={index} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="text-blue-500 flex-shrink-0 mt-0.5" aria-hidden="true">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Keyboard Shortcuts */}
        <section className="bg-white rounded-xl border border-gray-200 p-6" aria-labelledby="howto-heading">
          <h2 id="howto-heading" className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Keyboard className="w-5 h-5 text-orange-500" aria-hidden="true" />
            Keyboard Shortcuts
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left" role="table">
              <thead>
                <tr className="border-b border-gray-200">
                  <th scope="col" className="py-2 pr-4 font-semibold text-gray-900">Key</th>
                  <th scope="col" className="py-2 font-semibold text-gray-900">Action</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                <tr className="border-b border-gray-100">
                  <td className="py-2 pr-4"><kbd className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">Tab</kbd></td>
                  <td className="py-2">Navigate to next interactive element</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-2 pr-4"><kbd className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">Shift + Tab</kbd></td>
                  <td className="py-2">Navigate to previous interactive element</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-2 pr-4"><kbd className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">Enter</kbd></td>
                  <td className="py-2">Activate buttons and links</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-2 pr-4"><kbd className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">Esc</kbd></td>
                  <td className="py-2">Close modals and menus</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4"><kbd className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">Arrow keys</kbd></td>
                  <td className="py-2">Navigate within menus and dropdowns</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Known Limitations */}
        <section className="bg-white rounded-xl border border-gray-200 p-6" aria-labelledby="limitations-heading">
          <h2 id="limitations-heading" className="text-xl font-bold text-gray-900 mb-4">
            Known Limitations
          </h2>
          <p className="text-gray-700 mb-4">
            While we strive for full accessibility, some content may have limitations:
          </p>
          <ul className="space-y-2 text-gray-700" role="list">
            <li className="flex items-start gap-2" role="listitem">
              <span className="text-yellow-500 mt-1" aria-hidden="true">•</span>
              <span>AI-generated images may not have complete alt text descriptions</span>
            </li>
            <li className="flex items-start gap-2" role="listitem">
              <span className="text-yellow-500 mt-1" aria-hidden="true">•</span>
              <span>Video content may not have captions in all cases</span>
            </li>
            <li className="flex items-start gap-2" role="listitem">
              <span className="text-yellow-500 mt-1" aria-hidden="true">•</span>
              <span>Some third-party integrations may have their own accessibility limitations</span>
            </li>
          </ul>
          <p className="text-gray-700 mt-4">
            We are continuously working to improve these areas.
          </p>
        </section>

        {/* Last Updated */}
        <div className="text-center text-sm text-gray-500 pt-4">
          <p>This accessibility statement was last updated on {currentDate}</p>
        </div>

        {/* Back Link */}
        <div className="text-center pt-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 rounded"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default AccessibilityStatement;